import { useCallback, useMemo } from 'react';
import type { Editor, Range } from '@tiptap/react';
import type { EmojiItem } from '@tiptap/extension-emoji';
import type {
  SuggestionItem,
  SuggestionMenuProps,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-utils/suggestion-menu';
import { SuggestionMenu } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-utils/suggestion-menu';
import { getFilteredEmojis } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/emoji-menu';
import {
  IconPicker,
  type IconPickerSelection,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/icon-picker/icon-picker';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import type { CustomIconAttributes } from '@examdojo/core/tiptap';

export type EmojiDropdownMenuProps = Omit<SuggestionMenuProps, 'items' | 'children'>;

export const EmojiDropdownMenu = (props: EmojiDropdownMenuProps) => {
  return (
    <SuggestionMenu
      char=":"
      pluginKey="emojiDropdownMenu"
      decorationClass="tiptap-emoji-decoration"
      selector="tiptap-emoji-dropdown-menu"
      items={getSuggestionItems}
      maxHeight={440}
      {...props}
    >
      {(renderProps) => <UnifiedIconPicker onSuggestionSelect={renderProps.onSelect} />}
    </SuggestionMenu>
  );
};

const getSuggestionItems = async (props: { query: string; editor: Editor }) => {
  const emojis: EmojiItem[] = props.editor.extensionStorage.emoji.emojis || [];
  const filteredEmojis = getFilteredEmojis({ query: props.query, emojis });

  const items: SuggestionItem[] = filteredEmojis.map((emoji) => ({
    title: emoji.name,
    subtext: emoji.shortcodes.join(', '),
    context: emoji,
    onSelect: () => {},
  }));

  // Always return at least one item so the suggestion popup stays open
  // (e.g. when the user is on the Icons or FA tab and the query doesn't match any emoji).
  if (items.length === 0) {
    items.push({ title: '', onSelect: () => {} });
  }

  return items;
};

function UnifiedIconPicker({ onSuggestionSelect }: { onSuggestionSelect: (item: SuggestionItem) => void }) {
  const { editor } = useTiptapEditor();
  const emojis = useMemo<EmojiItem[]>(() => {
    return (editor?.storage?.emoji?.emojis as EmojiItem[]) || [];
  }, [editor]);

  const handleSelect = useCallback(
    (selection: IconPickerSelection) => {
      let syntheticItem: SuggestionItem;

      switch (selection.type) {
        case 'emoji':
          syntheticItem = {
            title: selection.emoji.name,
            context: selection.emoji,
            onSelect: (selectProps: { editor: Editor; range: Range; context?: EmojiItem }) => {
              if (!selectProps.editor || !selectProps.range || !selectProps.context) return;
              selectProps.editor.chain().focus().setEmoji(selectProps.context.name).run();
            },
          };
          break;
        case 'fa':
          syntheticItem = {
            title: selection.name,
            onSelect: (selectProps: { editor: Editor; range: Range }) => {
              if (!selectProps.editor) return;
              selectProps.editor
                .chain()
                .focus()
                .setCustomIcon({
                  iconType: 'fa',
                  name: selection.name,
                  color: selection.color,
                  showBackground: selection.showBackground,
                } as CustomIconAttributes)
                .run();
            },
          };
          break;
        case 'img':
          syntheticItem = {
            title: selection.name,
            onSelect: (selectProps: { editor: Editor; range: Range }) => {
              if (!selectProps.editor) return;
              selectProps.editor
                .chain()
                .focus()
                .setCustomIcon({ iconType: 'img', name: selection.src } as CustomIconAttributes)
                .run();
            },
          };
          break;
      }

      onSuggestionSelect(syntheticItem);
    },
    [onSuggestionSelect],
  );

  return <IconPicker emojis={emojis} onSelect={handleSelect} showSearch />;
}
